package ai.jessie.web.invoices

import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// UC-015 for one invoice that is not a Draft. The server decides eligibility (A5) and access (A7);
// state lives only as long as this invoice of this organisation is shown.
@Composable
internal fun issuedInvoiceArtefacts(
    organisationId: String,
    invoiceId: String,
    accessLost: (OnboardingProblem) -> Unit,
) {
    val port = LocalInvoiceArtefactPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, invoiceId) {
            InvoiceArtefactState(port, scope, organisationId, invoiceId, accessLost)
        }
    DisposableEffect(model) { onDispose { model.dispose() } }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    invoiceArtefacts(model.state, model.actions)
}

@Composable
internal fun invoiceArtefacts(state: InvoiceArtefactUiState, actions: InvoiceArtefactActions) {
    // `06-Rechnung.png` side column ("Anhänge"): the server-generated PDF and the optional work
    // report. No client-side preview; the PDF is the preview (UC-015).
    Section(attrs = CardStyle.toAttrs { attr("aria-labelledby", ARTEFACTS_TITLE_ID) }) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(attrs = { id(ARTEFACTS_TITLE_ID) }) { Text("PDF-Dokumente") }
        }
        Div(attrs = CardBodyStyle.toAttrs()) { artefactBody(state, actions) }
    }
}

@Composable
private fun artefactBody(state: InvoiceArtefactUiState, actions: InvoiceArtefactActions) {
    artefactProblem(state.error)
    if (state.busy)
        P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
            Text("PDF wird abgerufen oder erstellt …")
        }
    state.notice?.let { P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(it) } }
    val artefacts = state.artefacts
    if (artefacts == null) {
        // A8: the retry after a failed retrieval or generation.
        if (state.error != null)
            Div(attrs = RowStyle.toAttrs()) {
                themedButton("PDF-Status erneut laden", actions.refresh, disabled = state.busy)
            }
        return
    }
    // Steps 4 and 8; A6.
    if (artefacts.invoicePdf == PDF_SNAPSHOT_UNAVAILABLE)
        P(attrs = HintStyle.toAttrs()) { Text(snapshotUnavailableProblem().message) }
    else {
        Div(attrs = RowStyle.toAttrs()) {
            themedButton(
                "Rechnungs-PDF abrufen",
                actions.invoicePdf,
                variant = ButtonVariant.PRIMARY,
                disabled = state.busy,
                elementId = INVOICE_PDF_ID,
            )
        }
        state.invoicePdf?.let { artefactLinks("Rechnungs-PDF", it, INVOICE_PDF_OPEN_ID) }
    }
    workReport(artefacts.workReport, state, actions)
}

// Steps 5-7 and A2-A4: offered only for imported work, requested only after confirmation.
@Composable
private fun workReport(
    status: String,
    state: InvoiceArtefactUiState,
    actions: InvoiceArtefactActions,
) {
    // After confirming or cancelling, keyboard focus returns to the report control; the first
    // render leaves focus with the page title. Composed first, so a report link that arrives in the
    // same frame takes focus after it.
    val focusReturn = remember { FocusReturn() }
    LaunchedEffect(state.confirming) {
        if (focusReturn.pending && !state.confirming && !AppGlobals.isExporting)
            (document.getElementById(WORK_REPORT_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
    when (status) {
        REPORT_NO_IMPORTED_WORK ->
            P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                Text("Die Rechnung enthält keine importierte Arbeit.")
            }
        REPORT_UNAVAILABLE ->
            P(attrs = HintStyle.toAttrs()) { Text(workReportUnavailableProblem().message) }
        REPORT_AVAILABLE ->
            if (state.confirming) workReportConfirmation(state.busy, actions)
            else workReportButton("Arbeitsrapport anfordern", state.busy, actions.requestWorkReport)
        ARTEFACT_LINKED -> {
            workReportButton("Arbeitsrapport abrufen", state.busy, actions.workReport)
            state.workReport?.let { artefactLinks("Arbeitsrapport", it, WORK_REPORT_OPEN_ID) }
        }
    }
}

@Composable
private fun workReportConfirmation(busy: Boolean, actions: InvoiceArtefactActions) {
    Div(
        attrs =
            listOf(CardStyle, CardBodyStyle).toAttrs {
                id(WORK_REPORT_CONFIRMATION_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", "work-report-question")
                attr("tabindex", "-1")
            }
    ) {
        P(attrs = SmallStyle.toAttrs { id("work-report-question") }) {
            Text(
                "Separaten Arbeitsrapport aus der festgehaltenen Arbeit dieser Rechnung erstellen? " +
                    "Das Rechnungs-PDF bleibt unverändert."
            )
        }
        Div(attrs = RowStyle.toAttrs()) {
            themedButton(
                "Arbeitsrapport erstellen",
                actions.confirmWorkReport,
                variant = ButtonVariant.PRIMARY,
                disabled = busy,
            )
            themedButton("Abbrechen", actions.cancelWorkReport, disabled = busy)
        }
    }
    LaunchedEffect(Unit) {
        if (!AppGlobals.isExporting)
            (document.getElementById(WORK_REPORT_CONFIRMATION_ID) as? HTMLElement)?.focus()
    }
}

// Only a completely received PDF is linked; the new link takes keyboard focus.
@Composable
private fun artefactLinks(label: String, file: InvoiceArtefactFile, openId: String) {
    P(attrs = listOf(RowStyle, SmallStyle).toAttrs()) {
        A(
            href = file.url,
            attrs = {
                id(openId)
                attr("target", "_blank")
                attr("rel", "noopener")
            },
        ) {
            Text("$label öffnen")
        }
        Text(" · ")
        A(href = file.url, attrs = { attr("download", file.fileName) }) {
            Text("$label herunterladen (${file.fileName})")
        }
    }
    LaunchedEffect(file.url) {
        if (!AppGlobals.isExporting) (document.getElementById(openId) as? HTMLElement)?.focus()
    }
}

@Composable
private fun artefactProblem(problem: OnboardingProblem?) {
    if (problem == null) return
    P(
        attrs =
            AlertStyle.toAttrs {
                id(ARTEFACTS_ERROR_ID)
                attr("role", "alert")
                attr("tabindex", "-1")
            }
    ) {
        Text(problem.message)
    }
    LaunchedEffect(problem) {
        if (!AppGlobals.isExporting)
            (document.getElementById(ARTEFACTS_ERROR_ID) as? HTMLElement)?.focus()
    }
}

@Composable
private fun workReportButton(label: String, busy: Boolean, action: () -> Unit) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(label, action, disabled = busy, elementId = WORK_REPORT_ID)
    }
}

private const val ARTEFACTS_TITLE_ID = "invoice-artefacts-title"
private const val ARTEFACTS_ERROR_ID = "invoice-artefacts-error"
private const val INVOICE_PDF_ID = "invoice-pdf"
private const val INVOICE_PDF_OPEN_ID = "invoice-pdf-open"
private const val WORK_REPORT_ID = "invoice-work-report"
private const val WORK_REPORT_OPEN_ID = "invoice-work-report-open"
private const val WORK_REPORT_CONFIRMATION_ID = "invoice-work-report-confirmation"
